import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

interface PwaContextValue {
  online: boolean;
  installAvailable: boolean;
  install: () => Promise<boolean>;
  needRefresh: boolean;
  update: () => Promise<void>;
  dismissUpdate: () => void;
  updateError: boolean;
}

const PwaContext = createContext<PwaContextValue | null>(null);

export function PwaProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [updateError, setUpdateError] = useState(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError: () => setUpdateError(true),
  });

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  const value = useMemo<PwaContextValue>(
    () => ({
      online,
      installAvailable:
        Boolean(installPrompt) && !window.matchMedia('(display-mode: standalone)').matches,
      install: async () => {
        if (!installPrompt) return false;
        await installPrompt.prompt();
        const result = await installPrompt.userChoice;
        if (result.outcome === 'accepted') setInstallPrompt(null);
        return result.outcome === 'accepted';
      },
      needRefresh,
      update: async () => {
        try {
          setUpdateError(false);
          await updateServiceWorker(true);
        } catch {
          setUpdateError(true);
        }
      },
      dismissUpdate: () => setNeedRefresh(false),
      updateError,
    }),
    [installPrompt, needRefresh, online, setNeedRefresh, updateServiceWorker, updateError],
  );

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}

export function usePwa(): PwaContextValue {
  const value = useContext(PwaContext);
  if (!value) throw new Error('PwaProvider is missing');
  return value;
}
